import type { ReactElement } from 'react';
import { UNITS, type Unit } from '../../api/types.js';
import { IngredientAutocomplete } from './IngredientAutocomplete.js';

export interface IngredientRow {
  ingredientId: number | null;
  amount: number;
  unit: Unit;
}

export interface IngredientRowEditorProps {
  row: IngredientRow;
  index: number;
  onChange: (next: IngredientRow) => void;
  onRemove: () => void;
  onCreateRequested?: (name: string) => void;
}

export function IngredientRowEditor({
  row,
  index,
  onChange,
  onRemove,
  onCreateRequested,
}: IngredientRowEditorProps): ReactElement {
  const inputStyle = {
    background: 'var(--bg-primary, transparent)',
    borderColor: 'var(--border)',
    color: 'var(--text-main)',
  };

  return (
    <div
      className="grid grid-cols-12 items-center gap-2"
      data-testid={`ingredient-row-${index}`}
    >
      <div className="col-span-7">
        <IngredientAutocomplete
          value={row.ingredientId}
          onChange={(id) => onChange({ ...row, ingredientId: id })}
          onCreateRequested={onCreateRequested}
          testId={`ingredient-autocomplete-${index}`}
        />
      </div>
      <input
        type="number"
        min="0"
        step="0.1"
        value={row.amount === 0 ? '' : String(row.amount)}
        placeholder="Amount"
        onChange={(e) => {
          const n = Number.parseFloat(e.target.value);
          onChange({ ...row, amount: Number.isFinite(n) ? n : 0 });
        }}
        className="col-span-2 rounded-md border px-2 py-2 text-sm"
        style={inputStyle}
        data-testid={`ingredient-amount-${index}`}
      />
      <select
        value={row.unit}
        onChange={(e) => onChange({ ...row, unit: e.target.value as Unit })}
        className="col-span-2 rounded-md border px-2 py-2 text-sm"
        style={inputStyle}
        data-testid={`ingredient-unit-${index}`}
      >
        {UNITS.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove ingredient"
        className="col-span-1 flex min-h-[40px] items-center justify-center rounded-md border"
        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
        data-testid={`ingredient-remove-${index}`}
      >
        ×
      </button>
    </div>
  );
}